import { useNavigate } from 'react-router';
import { logApplicationInsightsError } from '../../common/functions/applicationInsightFunctions';
import { useQuery, skipToken } from '@tanstack/react-query';
import { IApplicationData, IApplicationProperties } from '@apply/api/src/api/ApplyApi';
import { applicationQueryKey } from '../../queries/applicationQueries';
import { AttachmentHandleMode } from '../components/FileOrFormQuestions/types/AttachmentHandleMode';
import { LoanType } from '@apply/constants';
import { useAuthentication } from '../../Login/hooks/useAuthentication';

export enum CurrentApplicationPage {
    Borrower,
    Property,
    SelectBudget,
    Budget,
    SelectSpecSheet,
    SpecSheet,
    Review,
    Finalize,
}

export interface NavigateApplicationParameters {
    page: CurrentApplicationPage;
    entryGuid?: string;
    propertyGuid?: string;
}

export const useNavigateApplication = ({ page, entryGuid, propertyGuid = '' }: NavigateApplicationParameters) => {
    const navigate = useNavigate();

    const { authentication } = useAuthentication();

    const queryKey = applicationQueryKey(entryGuid ?? '');

    const { data: loanType } = useQuery<IApplicationData, Error, LoanType | null>({
        queryKey,
        queryFn: skipToken,
        select: (d) => d.information?.borrower?.loanType ?? null,
    });

    const { data: properties } = useQuery<IApplicationData, Error, IApplicationProperties | undefined>({
        queryKey,
        queryFn: skipToken,
        select: (d) => d.information?.properties,
    });

    const { data: budgetFormSelected } = useQuery<IApplicationData, Error, AttachmentHandleMode | undefined>({
        queryKey,
        queryFn: skipToken,
        select: (d) =>
            d.information?.properties?.items?.find((p) => p.guid === propertyGuid)?.nonRental?.budget
                ?.handleMode as AttachmentHandleMode | undefined,
    });

    const { data: fixFlipBudgetFormSelected } = useQuery<IApplicationData, Error, AttachmentHandleMode | undefined>({
        queryKey,
        queryFn: skipToken,
        select: (d) =>
            d.information?.properties?.items?.find((p) => p.guid === propertyGuid)?.nonRental?.fixFlipBudget
                ?.handleMode as AttachmentHandleMode | undefined,
    });

    const { data: landBudgetFormSelected } = useQuery<IApplicationData, Error, AttachmentHandleMode | undefined>({
        queryKey,
        queryFn: skipToken,
        select: (d) =>
            d.information?.properties?.items?.find((p) => p.guid === propertyGuid)?.nonRental?.landBudget
                ?.handleMode as AttachmentHandleMode | undefined,
    });

    const { data: specSheetFormSelected } = useQuery<IApplicationData, Error, AttachmentHandleMode | undefined>({
        queryKey,
        queryFn: skipToken,
        select: (d) =>
            d.information?.properties?.items?.find((p) => p.guid === propertyGuid)?.nonRental?.specSheet
                ?.handleMode as AttachmentHandleMode | undefined,
    });

    const { data: constructionBudgetGuid } = useQuery<IApplicationData, Error, string | undefined>({
        queryKey,
        queryFn: skipToken,
        select: (d) =>
            d.information?.properties?.forms?.find((f) => f.type === 'Budget' && f.propertyGuid === propertyGuid)?.guid,
    });

    const { data: fixFlipBudgetGuid } = useQuery<IApplicationData, Error, string | undefined>({
        queryKey,
        queryFn: skipToken,
        select: (d) =>
            d.information?.properties?.forms?.find((f) => f.type === 'Fix-Flip' && f.propertyGuid === propertyGuid)
                ?.guid,
    });

    const { data: landBudgetGuid } = useQuery<IApplicationData, Error, string | undefined>({
        queryKey,
        queryFn: skipToken,
        select: (d) =>
            d.information?.properties?.forms?.find(
                (f) => f.type === 'Land Budget' && f.propertyGuid === propertyGuid
            )?.guid,
    });

    const { data: specSheetGuid } = useQuery<IApplicationData, Error, string | undefined>({
        queryKey,
        queryFn: skipToken,
        select: (d) =>
            d.information?.properties?.forms?.find((f) => f.type === 'Spec Sheet' && f.propertyGuid === propertyGuid)
                ?.guid,
    });

    const navigateApplicationForward = () => {
        window.scrollTo(0, 0);
        if (!entryGuid) throw new Error('Entry GUID required for navigation');

        switch (page) {
            case CurrentApplicationPage.Borrower:
                borrowerForward();
                break;
            case CurrentApplicationPage.Property:
                propertyForward();
                break;
            case CurrentApplicationPage.SelectBudget:
                selectBudgetForward();
                break;
            case CurrentApplicationPage.Budget:
                budgetForward();
                break;
            case CurrentApplicationPage.SelectSpecSheet:
                selectSpecSheetForward();
                break;
            case CurrentApplicationPage.SpecSheet:
                specSheetForward();
                break;
            case CurrentApplicationPage.Review:
                reviewForward();
                break;
            case CurrentApplicationPage.Finalize:
                finalizeForward();
                break;
            default: {
                const errorMessage = new Error('Could not navigate from unknown page');
                logApplicationInsightsError(errorMessage);
                throw errorMessage;
            }
        }
    };

    const navigateApplicationBackward = () => {
        window.scrollTo(0, 0);
        if (!entryGuid) throw new Error('Entry GUID required for navigation');

        switch (page) {
            case CurrentApplicationPage.Borrower:
                borrowerBackward();
                break;
            case CurrentApplicationPage.Property:
                propertyBackward();
                break;
            case CurrentApplicationPage.SelectBudget:
                selectBudgetBackward();
                break;
            case CurrentApplicationPage.Budget:
                budgetBackward();
                break;
            case CurrentApplicationPage.SelectSpecSheet:
                selectSpecSheetBackward();
                break;
            case CurrentApplicationPage.SpecSheet:
                specSheetBackward();
                break;
            case CurrentApplicationPage.Review:
                reviewBackward();
                break;
            case CurrentApplicationPage.Finalize:
                finalizeBackward();
                break;
            default: {
                const errorMessage = new Error('Could not navigate from unknown page');
                logApplicationInsightsError(errorMessage);
                throw errorMessage;
            }
        }
    };

    const borrowerForward = () => {
        if (propertyGuid) {
            navigate(`/property/${entryGuid}?pid=${propertyGuid}`);
        } else {
            navigate(`/property/${entryGuid}`);
        }
    };

    const borrowerBackward = () => {
        navigate('home');
    };

    const propertyForward = () => {
        validatePropertyGuid();

        if (budgetFormSelected) {
            if (budgetFormSelected === 'createnewform') {
                navigate(`/budget/budgetpage/${entryGuid}?pid=${propertyGuid}`);
                return;
            } else if (budgetFormSelected === 'editsavedform') {
                if (constructionBudgetGuid) {
                    navigate(`/budget/budgetpage/${entryGuid}?pid=${propertyGuid}`);
                } else {
                    navigate(`/budget/selectbudget/${entryGuid}?pid=${propertyGuid}`);
                }
                return;
            }
        }

        if (fixFlipBudgetFormSelected) {
            if (fixFlipBudgetFormSelected === 'createnewform') {
                navigate(`/budget/fixflipbudgetpage/${entryGuid}?pid=${propertyGuid}`);
                return;
            } else if (fixFlipBudgetFormSelected === 'editsavedform') {
                if (fixFlipBudgetGuid) {
                    navigate(`/budget/fixflipbudgetpage/${entryGuid}?pid=${propertyGuid}`);
                } else {
                    navigate(`/budget/selectbudget/${entryGuid}?pid=${propertyGuid}`);
                }
                return;
            }
        }

        if (landBudgetFormSelected) {
            if (landBudgetFormSelected === 'createnewform') {
                navigate(`/budget/landbudgetpage/${entryGuid}?pid=${propertyGuid}`);
                return;
            } else if (landBudgetFormSelected === 'editsavedform') {
                if (landBudgetGuid) {
                    navigate(`/budget/landbudgetpage/${entryGuid}?pid=${propertyGuid}`);
                } else {
                    navigate(`/budget/selectbudget/${entryGuid}?pid=${propertyGuid}`);
                }
                return;
            }
        }

        if (specSheetFormSelected) {
            if (specSheetFormSelected === 'createnewform') {
                navigate(`/specsheetpage/${entryGuid}?pid=${propertyGuid}`);
                return;
            } else if (specSheetFormSelected === 'editsavedform') {
                if (specSheetGuid) {
                    navigate(`/specsheetpage/${entryGuid}?pid=${propertyGuid}`);
                } else {
                    navigate(`/selectspecsheet/${entryGuid}?pid=${propertyGuid}`);
                }
                return;
            }
        }

        navigate(`/review/${entryGuid}`);
    };

    const propertyBackward = () => {
        validatePropertyGuid();
        navigate(`/borrower/${entryGuid}`);
    };

    const selectBudgetForward = () => {
        switch (loanType) {
            case LoanType.FixFlip: {
                navigate(`/budget/fixflipbudgetpage/${entryGuid}?pid=${propertyGuid}`);
                break;
            }
            case LoanType.Land: {
                navigate(`/budget/landbudgetpage/${entryGuid}?pid=${propertyGuid}`);
                break;
            }
            default: {
                navigate(`/budget/budgetpage/${entryGuid}?pid=${propertyGuid}`);
                break;
            }
        }
    };

    const selectBudgetBackward = () => {
        validatePropertyGuid();
        navigate(`/property/${entryGuid}?pid=${propertyGuid}`);
    };

    const budgetForward = () => {
        if (specSheetFormSelected === 'createnewform') {
            navigate(`/specsheetpage/${entryGuid}?pid=${propertyGuid}`);
            return;
        }
        if (specSheetFormSelected === 'editsavedform') {
            if (specSheetGuid) {
                navigate(`/specsheetpage/${entryGuid}?pid=${propertyGuid}`);
            } else {
                navigate(`/selectspecsheet/${entryGuid}?pid=${propertyGuid}`);
            }
            return;
        }
        navigate(`/review/${entryGuid}`);
    };

    const budgetBackward = () => {
        validatePropertyGuid();

        if (constructionBudgetGuid || fixFlipBudgetGuid || landBudgetGuid) {
            navigate(`/property/${entryGuid}?pid=${propertyGuid}`);
            return;
        }

        if (
            budgetFormSelected === 'editsavedform' ||
            fixFlipBudgetFormSelected === 'editsavedform' ||
            landBudgetFormSelected === 'editsavedform'
        ) {
            navigate(`/budget/selectbudget/${entryGuid}?pid=${propertyGuid}`);
            return;
        }

        navigate(`/property/${entryGuid}?pid=${propertyGuid}`);
    };

    const selectSpecSheetForward = () => {
        navigate(`/specsheetpage/${entryGuid}?pid=${propertyGuid}`);
    };

    const selectSpecSheetBackward = () => {
        validatePropertyGuid();
        if (isEnteredOnline(budgetFormSelected)) {
            navigate(`/budget/budgetpage/${entryGuid}?pid=${propertyGuid}`);
            return;
        }

        if (isEnteredOnline(fixFlipBudgetFormSelected)) {
            navigate(`/budget/fixflipbudgetpage/${entryGuid}?pid=${propertyGuid}`);
            return;
        }

        if (isEnteredOnline(landBudgetFormSelected)) {
            navigate(`/budget/landbudgetpage/${entryGuid}?pid=${propertyGuid}`);
            return;
        }

        navigate(`/property/${entryGuid}?pid=${propertyGuid}`);
    };

    const specSheetForward = () => {
        navigate(`/review/${entryGuid}`);
    };

    const specSheetBackward = () => {
        validatePropertyGuid();

        if (budgetFormSelected === 'createnewform') {
            navigate(`/budget/budgetpage/${entryGuid}?pid=${propertyGuid}`);
            return;
        } else if (budgetFormSelected === 'editsavedform') {
            if (constructionBudgetGuid) {
                navigate(`/budget/budgetpage/${entryGuid}?pid=${propertyGuid}`);
            } else {
                navigate(`/budget/selectbudget/${entryGuid}?pid=${propertyGuid}`);
            }
            return;
        }

        if (fixFlipBudgetFormSelected === 'createnewform') {
            navigate(`/budget/fixflipbudgetpage/${entryGuid}?pid=${propertyGuid}`);
            return;
        } else if (fixFlipBudgetFormSelected === 'editsavedform') {
            if (fixFlipBudgetGuid) {
                navigate(`/budget/fixflipbudgetpage/${entryGuid}?pid=${propertyGuid}`);
            } else {
                navigate(`/budget/selectbudget/${entryGuid}?pid=${propertyGuid}`);
            }
            return;
        }

        if (landBudgetFormSelected === 'createnewform') {
            navigate(`/budget/landbudgetpage/${entryGuid}?pid=${propertyGuid}`);
            return;
        } else if (landBudgetFormSelected === 'editsavedform') {
            if (landBudgetGuid) {
                navigate(`/budget/landbudgetpage/${entryGuid}?pid=${propertyGuid}`);
            } else {
                navigate(`/budget/selectbudget/${entryGuid}?pid=${propertyGuid}`);
            }
            return;
        }

        navigate(`/property/${entryGuid}?pid=${propertyGuid}`);
    };

    const reviewForward = () => {
        //double navigate is used in v1 - not sure why that's warranted for finalize but its in use
        navigate(`/finalize/${entryGuid}`, {
            replace: true,
        });
        navigate(`/finalize/${entryGuid}`);
    };

    const reviewBackward = () => {
        validatePropertyGuid();
        if (isEnteredOnline(specSheetFormSelected)) {
            navigate(`/specsheetpage/${entryGuid}?pid=${propertyGuid}`);
            return;
        }

        if (isEnteredOnline(budgetFormSelected)) {
            navigate(`/budget/budgetpage/${entryGuid}?pid=${propertyGuid}`);
            return;
        }

        if (isEnteredOnline(fixFlipBudgetFormSelected)) {
            navigate(`/budget/fixflipbudgetpage/${entryGuid}?pid=${propertyGuid}`);
            return;
        }

        if (isEnteredOnline(landBudgetFormSelected)) {
            navigate(`/budget/landbudgetpage/${entryGuid}?pid=${propertyGuid}`);
            return;
        }

        navigate(`/property/${entryGuid}?pid=${propertyGuid}`);
    };

    const finalizeForward = () => {
        if (!authentication?.user) return;
        const { requireUnderwritingFee, requireValuationFee } = authentication.user;

        const isUnderWritingFeeRequired =
            requireUnderwritingFee || (loanType !== LoanType.Construction && loanType !== LoanType.FixFlip);

        const isValuationFeeRequired =
            requireValuationFee || (loanType !== LoanType.Construction && loanType !== LoanType.FixFlip);

        if (properties?.items.every((p) => p.address.state === 'AZ')) {
            navigate(`/thankyou`);
        } else if (!isUnderWritingFeeRequired && !isValuationFeeRequired) {
            navigate(`/thankyou`);
        } else {
            navigate(`/appraisal/${entryGuid}`);
        }
    };

    const finalizeBackward = () => {
        navigate(`/review/${entryGuid}`);
    };

    const validatePropertyGuid = () => {
        if (!propertyGuid) throw new Error('Property GUID required for navigation');
    };

    const isEnteredOnline = (value: AttachmentHandleMode): boolean => {
        return value === 'createnewform' || value === 'editsavedform';
    };

    return { navigateApplicationForward, navigateApplicationBackward };
};
